export const ignoreOrderCompare = (a, b) => {
  if (a.length !== b.length) return false;
  const elements = new Set([...a, ...b]);
  for (const x of elements) {
    const count1 = a.filter((e) => e === x).length;
    const count2 = b.filter((e) => e === x).length;
    if (count1 !== count2) return false;
  }
  return true;
};

export const deepEqual = (object1, object2) => {
  const areObjects = isObject(object1) && isObject(object2);
  if (!areObjects) {
    return false;
  }
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);
  if (keys1.length !== keys2.length) {
    return false;
  }
  for (const key of keys1) {
    const val1 = object1[key];
    const val2 = object2[key];
    const areObjects = isObject(val1) && isObject(val2);
    if (
      (areObjects && !deepEqual(val1, val2)) ||
      (!areObjects && val1 !== val2)
    ) {
      return false;
    }
  }
  return true;
};

export const deepClone = (obj) => {
  if (!isObject(obj)) {
    return obj;
  }

  const clone = Array.isArray(obj) ? [] : {};

  for (let key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      clone[key] = deepClone(obj[key]);
    }
  }

  return clone;
};

const isObject = (object) => {
  return object != null && typeof object === "object";
};

export const handleError = (error) => {
  // Detect request cancellation (for fetch or axios with AbortController)
  if (error.name === "CanceledError" || error.name === "AbortError") {
    return {
      success: false,
      message: "canceled",
      error,
    };
  }

  const errorObject = {
    message: "An error occurred.",
    data: null,
    status: null,
    headers: null,
  };

  // Axios: server responded with error
  if (error.response) {
    errorObject.message = error.response.data?.message || "Server error";
    errorObject.data = error.response.data;
    errorObject.status = error.response.status;
    errorObject.headers = error.response.headers;

    return {
      success: false,
      message: errorObject.message,
      error: errorObject,
    };
  }

  // Axios: request was made but no response
  if (error.request) {
    errorObject.message = "No response received.";
    return {
      success: false,
      message: errorObject.message,
      error: errorObject,
    };
  }

  // Other unknown error (coding issue, setup error, etc.)
  errorObject.message = error.message || "Unknown error";
  return {
    success: false,
    message: errorObject.message,
    error: errorObject,
  };
};

/**
 * Builds a query string for a URL based on the provided query parameters.
 * @param {Object} params - The query parameters object.
 * @returns {string} The generated query string.
 */
export const buildQueryString = (params) => {
  const query = Object.entries(params)
    .filter((param) => param[1] && param)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");
  return query;
};

/**
 * Checks to see if a string is in a matching format for an ASIN.
 *
 * @param {string} string - The string to be checked.
 * @returns {boolean} True or false depending on the test.
 */
export const asinCheck = (string) => {
  // ASIN pattern
  const asinRegex = /^B[A-Z0-9]{9}$/;

  // ISBN-10 pattern
  const isbn10Regex = /^\d{10}$/;

  if (asinRegex.test(string) || isbn10Regex.test(string)) {
    return true;
  } else {
    return false;
  }
};

export const rotateImageSrc = (imageSrc, rotation) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = imageSrc;
    image.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Set canvas size to handle rotation
      canvas.width = image.height;
      canvas.height = image.width;

      // Translate and rotate the context
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.drawImage(image, -image.width / 2, -image.height / 2);

      // Get the rotated image data URL
      const rotatedImageSrc = canvas.toDataURL("image/jpeg");
      resolve(rotatedImageSrc);
    };
    image.onerror = (error) => reject(error);
  });
};

export const isDateExpired = (dateString) => {
  const now = new Date();
  const inputDate = new Date(dateString);

  return inputDate < now;
};
